'use client';

import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Crosshair, Loader2 } from 'lucide-react';
import { useAddressStore } from '@/store/address-store';
import { useMarketplaceStore } from '@/store/marketplace-store';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';

interface Place {
  name: string;
  formatted_address: string;
  place_id: string;
  geometry?: {
    location: {
      lat: number;
      lng: number;
    }
  }
}

export const SimpleAddressBar = () => {
  const { address, setAddress } = useAddressStore();
  const { config, setLocation } = useMarketplaceStore();
  const [places, setPlaces] = useState<Place[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Initialize input with address
  useEffect(() => {
    if (address && !searchQuery) {
      setSearchQuery(address);
    }
  }, [address]);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Search places with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 && searchQuery !== address) {
        handleSearchPlaces();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchPlaces = async () => {
    if (searchQuery.length < 3) return;
    
    setIsSearching(true);
    try {
      const googleKey = config?.settings?.webapp_google_api_key || config?.google_maps_api_key || "";
      const response = await fetch(
        `/api/places/search?query=${encodeURIComponent(searchQuery)}&key=${googleKey}`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch places');
      }
      
      const data = await response.json();
      setPlaces(data.results || []);
      setShowDropdown(data.results && data.results.length > 0);
    } catch (error) {
      console.error('Error searching places:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handlePlaceSelect = (place: Place) => {
    setSearchQuery(place.formatted_address);
    setShowDropdown(false);
    
    if (place.geometry?.location) {
      const { lat, lng } = place.geometry.location;
      console.log(lat, "lat ****", lng, "lng ****");
      setLocation(lat, lng, place.formatted_address);
      setAddress(place.formatted_address, { latitude: lat, longitude: lng });
    } else {
      setAddress(place.formatted_address);
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation not supported',
        description: 'Your browser does not support geolocation',
        variant: 'destructive'
      });
      return;
    }
    
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const googleKey = config?.settings?.webapp_google_api_key || config?.google_maps_api_key || "";
          
          if (googleKey) {
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${googleKey}`
            );
            
            if (!response.ok) throw new Error('Failed to get address from coordinates');
            
            const data = await response.json();
            if (data.results && data.results.length > 0) {
              const formattedAddress = data.results[0].formatted_address;
              setSearchQuery(formattedAddress);
              setLocation(latitude, longitude, formattedAddress);
              setAddress(formattedAddress, { latitude, longitude });
            }
          } else {
            const formattedAddress = `Lat: ${latitude.toFixed(6)}, Lng: ${longitude.toFixed(6)}`;
            setSearchQuery(formattedAddress);
            setLocation(latitude, longitude, formattedAddress);
            setAddress(formattedAddress, { latitude, longitude });
          }
        } catch (error) {
          console.error('Error getting current location:', error);
          toast({
            title: 'Error getting location',
            description: 'Please try again or enter address manually',
            variant: 'destructive'
          });
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setIsLocating(false);
        
        let errorMessage = 'Failed to get your location';
        if (error.code === 1) {
          errorMessage = 'Location access denied. Please allow location access.';
        } else if (error.code === 2) {
          errorMessage = 'Location unavailable. Please try again.';
        } else if (error.code === 3) {
          errorMessage = 'Location request timed out. Please try again.';
        }
        
        toast({
          title: 'Location Error',
          description: errorMessage,
          variant: 'destructive'
        });
      }
    );
  };

  return (
    <div className="relative w-full max-w-xl flex items-center">
      <div className="relative flex-1 bg-white rounded-md shadow-sm">
        <div className="absolute inset-y-0 left-3 flex items-center">
          <MapPin className="h-5 w-5 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Enter your delivery location"
          className="w-full h-10 pl-10 pr-3 py-2 border-none rounded-md focus:ring-1 focus:ring-gray-200 focus:outline-none"
        />
        {isSearching && (
          <div className="absolute inset-y-0 right-3 flex items-center">
            <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
          </div>
        )}
      </div>
      
      <Button
        onClick={getCurrentLocation}
        variant="secondary"
        size="sm"
        className="ml-2 whitespace-nowrap bg-[#900f21] text-white hover:bg-[#900f21]/90 h-10 flex items-center"
        disabled={isLocating}
      >
        {isLocating ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <Crosshair className="h-4 w-4 mr-1" />
        )}
        Locate Me
      </Button>
      
      {/* Dropdown for search results */}
      {showDropdown && (
        <div 
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg z-50 overflow-hidden max-h-60 overflow-y-auto"
        >
          {places?.map((place) => (
            <div
              key={place.place_id}
              className="px-4 py-2 cursor-pointer hover:bg-red-100"
              onClick={(e) => {
                e.stopPropagation();
                handlePlaceSelect(place);
              }}
            >
              <div className="font-medium"> {place.name}</div>
              <div className="text-sm text-gray-600">{place.formatted_address}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
